import * as d3 from "d3";
import { observer } from "mobx-react-lite";
import { useState, useMemo, useEffect } from "react";
import { XAxis } from "./XAxis";
import { YAxis } from "./YAxis";
import { LoadingOutlined } from "@ant-design/icons";
import moment from "moment";

import "./Stress.css";

function Stress2({
  sdnnBars,
  sdnnStats,
  isRealtime,
  lastHeartRate,
  lastSdnn,
  groupStatusColor,
  statusLabel,
  fill = "#303030",
}) {
  const width = 900;
  const height = 175;
  const [marginLeft, marginRight, marginTop, marginBottom] = [25, 15, 15, 15];
  const innerWidth = width - marginLeft - marginRight;
  const innerHeight = height - marginTop - marginBottom;

  const xScale = useMemo(() => {
    let min = d3.min(sdnnStats, (d) => new Date(d.end_date));
    let max = d3.max(sdnnStats, (d) => new Date(d.end_date));

    min = Math.min(d3.min(sdnnBars, (d) => new Date(d.timepoint)));
    max = Math.max(
      max,
      d3.max(sdnnBars, (d) => new Date(d.timepoint))
    );
    if (isRealtime) {
      max = moment(max).endOf("hour").toDate();
      min = moment(min).startOf("hour").toDate();
    }

    return d3.scaleTime().domain([min, max]).range([marginLeft, innerWidth]);
  }, [innerWidth, marginLeft, sdnnStats]);

  const yScale = useMemo(() => {
    const min = d3.min(sdnnStats, (d) => d.mean);
    const max = d3.max(sdnnStats, (d) => d.mean);
    const minB = d3.min(sdnnBars, (d) => d.min);
    const maxB = d3.max(sdnnBars, (d) => d.max);

    return d3
      .scaleLinear()
      .domain([Math.min(min, minB), Math.max(max, maxB)])
      .range([innerHeight, marginTop]);
  }, [sdnnStats, sdnnBars, innerHeight, marginTop]);

  const colorFor = (color) => {
    if (color === "red") {
      return "rgba(255, 0, 0, 0.5)";
    } else if (color === "yellow") {
      return "rgb(255, 241, 169)";
    } else if (color === "green") {
      return "rgba(0, 182, 24, 0.5)";
    } else {
      return color;
    }
  };

  return (
    <>
      {sdnnStats && (
        <div className="Stress_chart" style={{ display: "block" }}>
          <div style={{ display: "flex" }}>
            {isRealtime && (
              <div className="Stress_chart__Info">
                <p className="Stress_chart__Info__title">HeartRate</p>
                <p className="Stress_chart__Info__bpmValue">{lastHeartRate}</p>
                <p className="Stress_chart__Info__bpm">bpm</p>
              </div>
            )}

            {isRealtime && lastSdnn && (
              <div className="Stress_chart__Info">
                <p className="Stress_chart__Info__title">Stress</p>
                <p className="Stress_chart__Info__bpmValue">
                  {lastSdnn.toFixed(1)}
                </p>
                <p className="Stress_chart__Info__bpm">sdnn</p>
              </div>
            )}
          </div>
          <div className="Stress_chart__Chart">
            <svg className="Sparkline" viewBox={`0 0 ${width} ${height}`}>
              <g>
                <XAxis
                  xScale={xScale}
                  innerHeight={innerHeight + marginBottom - 5}
                ></XAxis>
                <YAxis
                  margin={marginLeft}
                  yScale={yScale}
                  innerWidth={innerWidth}
                ></YAxis>
              </g>

              <g>
                {groupStatusColor &&
                  groupStatusColor.map((d, i) => (
                    <g>
                      <rect
                        className="Stress__bar"
                        x={Math.max(
                          xScale.range()[0],
                          xScale(new Date(d[0].timestamp))
                        )}
                        value={d[0].timestamp}
                        y={0}
                        height={10}
                        width={
                          Math.min(
                            xScale.range()[1],
                            xScale(new Date(d[1].timestamp))
                          ) -
                          Math.min(
                            xScale.range()[1],
                            xScale(new Date(d[0].timestamp))
                          )
                        }
                        key={`rect-${d[0].timestamp}`}
                        fill={colorFor(d[0].value)}
                        strokeWidth={"0.5px"}
                      ></rect>
                    </g>
                  ))}
              </g>

              <g>
                {sdnnBars.map((d, i) => (
                  <g>
                    <rect
                      x={xScale(new Date(d.timepoint))}
                      value={d.timepoint}
                      y={yScale(d.max)}
                      height={-yScale(d.max) + yScale(d.min)}
                      width={Math.min(
                        5,
                        Math.abs(innerWidth / sdnnBars.length - 3)
                      )}
                      key={`rect-${d.timepoint}`}
                      className="Stress__bar"
                      fill={fill}
                    ></rect>
                    <circle
                      cx={
                        xScale(new Date(d.timepoint)) +
                        Math.min(
                          5,
                          Math.abs(innerWidth / sdnnBars.length - 3)
                        ) /
                          2
                      }
                      cy={yScale(d.median)}
                      r="1"
                      fill="rgba(255,255,255, 1)"
                      stroke="white"
                      strokeWidth={"0px"}
                    ></circle>
                  </g>
                ))}
              </g>
            </svg>
          </div>
        </div>
      )}
    </>
  );
}

export default observer(Stress2);
